import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ProgressBar,
  RoundDoneIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components';
import { SuccessModal } from '@/components/elements';
import { GoPaperless } from '@/modules/transaction/components/Statements/components/GoPaperless';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import EditEmployeeAddressForm from '@/modules/user/components/ManageProfile/components/ManageProfileDashboard/components/EditEmployeeAddress/EditEmployeeAddressForm';
import { StatementType } from '@/modules/user/user.types';

import ReturnedItemsStep from './components/ReturnedItemsStep';
import ReviewStep from './components/ReviewStep';
import useCanEditAddress from './hooks/useCanEditAddress';
import useReturnedChecks from './hooks/useReturnedChecks';
import useReviewAddress from './hooks/useReviewAddress';
import useReviewStepNavigation, { ReviewStep as ReviewStepEnum } from './hooks/useReviewStepNavigation';
import useReviewSuccess from './hooks/useReviewSuccess';
import useTermsAgreement from './hooks/useTermsAgreement';
import useReturnedItemsQuery from './queries/useReturnedItems.query';
import { useBadAddressReviewStore } from './stores/useBadAddressReview.store';

interface Props {
  storeKey?: string;
}

const ReviewAddressModal: React.FC<Props> = ({ storeKey }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const { isModalVisible, handleOpenModal, handleCloseModal } = useBadAddressReviewStore(storeKey);
  const { canEdit, isAddressEditingAllowed, statementType } = useCanEditAddress();
  const { hasQualifyingLetters, providerChecks, participantChecks } = useReturnedItemsQuery();
  const { organizationProperties } = useOrganizationProperties();
  const hsaCustodialLink = organizationProperties?.custodialAgreementUrl || '';

  const {
    employee,
    currentAddress,
    formattedAddress,
    isUndeliverable,
    isConfirming,
    confirmAddressById,
    refetch,
  } = useReviewAddress();

  const {
    termsAccepted,
    isAgreementOpened,
    isWarningShaking,
    setTermsAccepted,
    handleOpenAgreement,
    triggerWarningShake,
    resetTermsAgreement,
  } = useTermsAgreement(hsaCustodialLink);

  const {
    hasChecks,
    isProcessingChecks,
    areCheckDecisionsComplete,
    checksProps,
    processChecksIfNeeded,
    resetCheckDecisions,
  } = useReturnedChecks({
    providerChecks,
    participantChecks,
    employeeId: employee?.id ? `${employee.id}` : undefined,
    excludeCheckPaymentMethod: isUndeliverable && !canEdit,
  });

  const {
    isGoPaperlessVisible,
    isSuccessVisible,
    successTitle,
    successHelpText,
    showGoPaperless,
    hideGoPaperless,
    showSuccessWithDelay,
    hideSuccess,
  } = useReviewSuccess({
    isChecksProcessedAddressPending: hasChecks && isUndeliverable && !canEdit,
  });

  const canOfferPaperless = !employee?.isElectronicStatementOnly
    && statementType === StatementType.PAPER_AND_ELECTRONIC;
  const hasQualifyingLettersFlag = !!hasQualifyingLetters;

  const {
    activeStep,
    selectedOption,
    isReachOutEmployer,
    stepperOptions,
    isNextDisabled,
    nextButtonLabel,
    stepTitle,
    setSelectedOption,
    handleNext,
    handleBack,
    handleClose,
    handleEditSuccess,
    handleGoPaperlessBack,
    handleGoPaperlessSubmit,
    handleSuccessClose,
  } = useReviewStepNavigation({
    canEdit: !!canEdit,
    isAddressEditingAllowed: !!isAddressEditingAllowed,
    canOfferPaperless: !!canOfferPaperless,
    hasChecks,
    hasQualifyingLetters: hasQualifyingLettersFlag,
    termsAccepted: !!termsAccepted,
    isConfirming: !!isConfirming,
    isProcessingChecks: !!isProcessingChecks,
    areCheckDecisionsComplete,
    closeModal: handleCloseModal,
    openModal: handleOpenModal,
    confirmAddressIfNeeded: confirmAddressById,
    processChecksIfNeeded,
    resetChecks: resetCheckDecisions,
    resetTerms: resetTermsAgreement,
    showGoPaperless,
    hideGoPaperless,
    showSuccessWithDelay,
    onSuccessClose: async () => {
      hideSuccess();
      await refetch();
    },
  });

  const isFirstStep = activeStep === ReviewStepEnum.REVIEW;

  return (
    <>
      {isModalVisible && (
        <ModalWrapper visible onSetVisible={handleClose} isClosingByClickOutsideBlocked>
          <Box margin={{ bottom: isMobile ? 'spacing16' : 'spacing24' }} align="center">
            <Box width={stepperOptions.length >= 3 ? '560px' : '280px'}>
              <ProgressBar activeKey={activeStep} steps={stepperOptions} />
            </Box>
          </Box>

          <Box align="center" margin={{ bottom: isMobile ? 'spacing16' : 'spacing24' }}>
            <Text
              size={isMobile ? 'xxlarge' : '2xl'}
              color="textBody"
              weight="bold"
              textAlign="center"
              data-testid="bad-address-review_step-title"
            >
              {stepTitle}
            </Text>
          </Box>

          {activeStep === ReviewStepEnum.REVIEW && (
            <ReviewStep
              formattedAddress={formattedAddress}
              selectedOption={selectedOption}
              onSelectOption={setSelectedOption}
            />
          )}

          {activeStep === ReviewStepEnum.EDIT && currentAddress && (
            <EditEmployeeAddressForm
              address={currentAddress}
              onClose={handleBack}
              onSuccess={handleEditSuccess}
              withoutSnackbar
            />
          )}

          {activeStep === ReviewStepEnum.RETURNED_ITEMS && (
            <ReturnedItemsStep
              isReachOutEmployer={isReachOutEmployer}
              hasQualifyingLetters={hasQualifyingLettersFlag}
              termsAccepted={termsAccepted}
              isAgreementOpened={isAgreementOpened}
              isWarningShaking={isWarningShaking}
              checksProps={checksProps}
              onOpenAgreement={handleOpenAgreement}
              onToggleTerms={() => setTermsAccepted(!termsAccepted)}
              onTriggerShake={triggerWarningShake}
            />
          )}

          {activeStep !== ReviewStepEnum.EDIT && (
            <Box
              direction={isMobile ? 'column-reverse' : 'row'}
              justify="end"
              gap="spacing12"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
              pad={isMobile ? { horizontal: 'spacing4' } : undefined}
            >
              <AppButton
                testId="bad-address-review_cancel-btn"
                onClick={isFirstStep ? handleClose : handleBack}
                buttonType="secondary"
                width={isMobile ? '100%' : '180px'}
              >
                {isFirstStep ? t('Cancel') : t('Back')}
              </AppButton>
              <AppButton
                testId="bad-address-review_next-btn"
                onClick={handleNext}
                disabled={isNextDisabled}
                width={isMobile ? '100%' : '180px'}
              >
                {nextButtonLabel}
              </AppButton>
            </Box>
          )}
        </ModalWrapper>
      )}

      {isGoPaperlessVisible && (
        <GoPaperless
          onSetVisible={hideGoPaperless}
          onExternalSubmit={handleGoPaperlessSubmit}
          onBack={handleGoPaperlessBack}
          isCheckboxOptional
        />
      )}

      {isSuccessVisible && (
        <SuccessModal
          icon={(
            <Box margin={{ bottom: 'spacing12' }}>
              <RoundDoneIcon />
            </Box>
          )}
          header={successTitle}
          helptext={successHelpText}
          visible
          onSetVisible={handleSuccessClose}
          buttonText={t('Close')}
          buttonStyle={{ width: isMobile ? '100%' : '200px' }}
          testId="bad-address-review_success-modal"
        />
      )}
    </>
  );
};

export default ReviewAddressModal;
